import { ref, computed, type Ref } from 'vue';
import type { Product, ProductCategory, ProductCategoryStatus } from '@/api/openapi';
import type {
  GetCallTargetProductCategoryAssociations200ResponseProductCategoryAssociationsInner,
} from '@/api/openapi/models/get-call-target-product-category-associations200-response-product-category-associations-inner';
import {
  CallTargetProductCategoryAssociationApiService,
  ProductCategoryApiService,
  ProductCategoryStatusApiService,
} from '@/api/user/resources/product_category';

export type ProductCategoryAssociation = GetCallTargetProductCategoryAssociations200ResponseProductCategoryAssociationsInner;

export const useProductCategoryAssociation = (callTargetId: Ref<number>) => {
  const associationApi = new CallTargetProductCategoryAssociationApiService();
  const categoryApi = new ProductCategoryApiService();
  const statusApi = new ProductCategoryStatusApiService();
  const associations = ref<ProductCategoryAssociation[]>([]);
  const productCategories = ref<ProductCategory[]>([]);
  const productCategoryStatuses = ref<ProductCategoryStatus[]>([]);
  const loading = ref(false);

  const unlinkedCategories = computed(() => {
    const linkedIds = new Set(associations.value.map(a => a.productCategoryId));
    return productCategories.value.filter(c => !linkedIds.has(c.id));
  });

  const hasAvailableCategories = computed(() => unlinkedCategories.value.length > 0);
  const hasAvailableStatuses = computed(() => productCategoryStatuses.value.length > 0);
  const isAddButtonDisabled = computed(() => !hasAvailableCategories.value || !hasAvailableStatuses.value);

  const getCategoryName = (productCategoryId: number): string => {
    return productCategories.value.find(c => c.id === productCategoryId)?.name ?? '';
  };

  const getProductsForCategory = (productCategoryId: number): Product[] => {
    return productCategories.value.find(c => c.id === productCategoryId)?.products ?? [];
  };

  const getLinkedProductIds = (association: ProductCategoryAssociation): number[] => {
    return association.callTargetProductAssociations.map(a => a.productId);
  };

  const getProductName = (productId: number, productCategoryId: number): string => {
    const products = getProductsForCategory(productCategoryId);
    return products.find(p => p.id === productId)?.name ?? '';
  };

  const refreshAssociations = async () => {
    const res = await associationApi.getAssociations(callTargetId.value);
    associations.value = res.data.productCategoryAssociations ?? [];
  };

  const fetchAll = async () => {
    loading.value = true;
    try {
      const [assocRes, catRes, statusRes] = await Promise.all([
        associationApi.getAssociations(callTargetId.value),
        categoryApi.getProductCategories(),
        statusApi.getProductCategoryStatuses(),
      ]);
      associations.value = assocRes.data.productCategoryAssociations ?? [];
      productCategories.value = catRes.data.productCategories ?? [];
      productCategoryStatuses.value = statusRes.data.productCategoryStatuses ?? [];
    } finally {
      loading.value = false;
    }
  };

  const addAssociation = async (productCategoryId: number, productCategoryStatusId: number) => {
    await associationApi.createAssociation(callTargetId.value, productCategoryId, productCategoryStatusId);
    await refreshAssociations();
  };

  const updateStatus = async (associationId: number, productCategoryStatusId: number) => {
    await associationApi.updateAssociationStatus(associationId, productCategoryStatusId);
    await refreshAssociations();
  };

  const addProductAssociation = async (associationId: number, productId: number) => {
    await associationApi.createProductAssociation(associationId, productId);
    await refreshAssociations();
  };

  const removeProductAssociation = async (association: ProductCategoryAssociation, productId: number) => {
    const productAssociation = association.callTargetProductAssociations.find(a => a.productId === productId);
    if (!productAssociation) return;
    await associationApi.deleteProductAssociation(association.id, productAssociation.id);
    await refreshAssociations();
  };

  const deleteAssociation = async (associationId: number) => {
    await associationApi.deleteAssociation(associationId);
    const res = await associationApi.getAssociations(callTargetId.value);
    associations.value = res.data.productCategoryAssociations ?? [];
  };

  return {
    associations,
    productCategories,
    productCategoryStatuses,
    loading,
    unlinkedCategories,
    hasAvailableCategories,
    hasAvailableStatuses,
    isAddButtonDisabled,
    getCategoryName,
    getProductsForCategory,
    getLinkedProductIds,
    getProductName,
    fetchAll,
    addAssociation,
    updateStatus,
    addProductAssociation,
    removeProductAssociation,
    deleteAssociation,
  };
};
